
import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Job } from "./useJobs";

export function useJobFilters() {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    searchQuery: "",
    locationFilter: "all",
    skillFilter: "all",
    companyFilter: "all",
    roleFilter: "all",
    workArrangementFilter: "all",
    salaryRange: [0, 200000] as [number, number], // Keep as a range but only use min
    industryFilter: "all"
  });

  const [activeFilters, setActiveFilters] = useState({
    searchQuery: "",
    locationFilter: "all",
    skillFilter: "all",
    companyFilter: "all",
    roleFilter: "all",
    workArrangementFilter: "all",
    salaryRange: [0, 200000] as [number, number],
    industryFilter: "all"
  });

  // Update search query from URL parameters
  useEffect(() => {
    const urlSearchQuery = searchParams.get("search");
    if (urlSearchQuery) {
      setFilters(prev => ({ ...prev, searchQuery: urlSearchQuery }));
      setActiveFilters(prev => ({ ...prev, searchQuery: urlSearchQuery }));
    }
  }, [searchParams]);

  const applyFilters = useCallback(() => {
    console.log('Applying filters:', filters);
    setActiveFilters(filters);
  }, [filters]);

  const resetFilters = useCallback(() => {
    // Reset all filters to default values, including searchQuery
    const defaultFilters = {
      searchQuery: "",
      locationFilter: "all",
      skillFilter: "all",
      companyFilter: "all",
      roleFilter: "all",
      workArrangementFilter: "all",
      salaryRange: [0, 200000] as [number, number],
      industryFilter: "all"
    };
    
    setFilters(defaultFilters);
    setActiveFilters(defaultFilters);
  }, []);

  const filterJobs = useCallback((jobs: Job[] | null) => {
    console.log("Filtering jobs with activeFilters:", activeFilters);
    if (!jobs) return [];
    
    return jobs.filter(job => {
      // Search query filter - enhanced to search through multiple fields
      const searchTerms = activeFilters.searchQuery.toLowerCase().split(' ');
      const matchesSearch = !activeFilters.searchQuery || searchTerms.every(term => 
        job.title.toLowerCase().includes(term) ||
        job.description.toLowerCase().includes(term) ||
        job.key_skills?.some(skill => skill.toLowerCase().includes(term)) ||
        job.location.toLowerCase().includes(term) ||
        job.organization?.organization?.toLowerCase().includes(term) ||
        job.organization?.full_name?.toLowerCase().includes(term)
      );

      // Location filter
      const matchesLocation = activeFilters.locationFilter === "all" || 
        job.location.toLowerCase().includes(activeFilters.locationFilter.toLowerCase());

      // Skill filter
      const matchesSkill = activeFilters.skillFilter === "all" || 
        (job.key_skills && job.key_skills.some(skill => 
          skill.toLowerCase().includes(activeFilters.skillFilter.toLowerCase())
        ));

      // Company filter
      const matchesCompany = activeFilters.companyFilter === "all" || 
        job.organization?.organization === activeFilters.companyFilter;

      // Role filter
      const matchesRole = activeFilters.roleFilter === "all" || 
        job.title.toLowerCase().includes(activeFilters.roleFilter.toLowerCase());

      // Work arrangement filter
      const matchesWorkArrangement = activeFilters.workArrangementFilter === "all" || 
        job.work_arrangement === activeFilters.workArrangementFilter;

      // Simplified Salary filter - check only minimum salary
      const matchesSalary = !job.salary_range_min || job.salary_range_min >= activeFilters.salaryRange[0];

      // Industry filter
      const matchesIndustry = activeFilters.industryFilter === "all" || 
        job.industry === activeFilters.industryFilter;

      const matches = matchesSearch && matchesLocation && matchesSkill && 
        matchesCompany && matchesRole && matchesWorkArrangement && 
        matchesSalary && matchesIndustry;

      if (!matches) {
        console.log('Job filtered out:', {
          jobId: job.id,
          jobTitle: job.title,
          filters: {
            matchesSearch,
            matchesLocation,
            matchesSkill,
            matchesCompany,
            matchesRole,
            matchesWorkArrangement,
            matchesSalary,
            matchesIndustry
          }
        });
      }

      return matches;
    });
  }, [activeFilters]);

  return {
    filters,
    setters: {
      setSearchQuery: (value: string) => setFilters(prev => ({ ...prev, searchQuery: value })),
      setLocationFilter: (value: string) => setFilters(prev => ({ ...prev, locationFilter: value })),
      setSkillFilter: (value: string) => setFilters(prev => ({ ...prev, skillFilter: value })),
      setCompanyFilter: (value: string) => setFilters(prev => ({ ...prev, companyFilter: value })),
      setRoleFilter: (value: string) => setFilters(prev => ({ ...prev, roleFilter: value })),
      setWorkArrangementFilter: (value: string) => setFilters(prev => ({ ...prev, workArrangementFilter: value })),
      setSalaryRange: (value: [number, number]) => setFilters(prev => ({ ...prev, salaryRange: value })),
      setIndustryFilter: (value: string) => setFilters(prev => ({ ...prev, industryFilter: value })),
    },
    filterJobs,
    applyFilters,
    resetFilters,
  };
}
